import { useEffect, useState } from 'react';
import axios from 'axios';
import { ExternalLink, Loader2 } from 'lucide-react';
import { TableCard } from '../../TableCard';
import { ModelStatsTable } from '../../ModelStatsTable';
import { FILTER_MODEL, GEN_MODELS, type NoticeModel } from '../../models';
import { PIPELINE } from '../../viewerPipelines';
import { useLanguage } from '../../../../contexts/LanguageContext';

// A thin used/total progress bar; turns rose once the limit is reached.
function Bar({ used, total }: { used: number; total: number }) {
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const over = total > 0 && used >= total;
  return (
    <div className="h-2.5 overflow-hidden rounded-full bg-slate-200">
      <div
        className={`h-full rounded-full transition-[width] ${over ? 'bg-rose-500' : 'bg-blue-500'}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

// Per-phase economics keyed by model id (cost / avg time / call count / tokens).
type PhaseStats = {
  costs: Record<string, number>;
  times: Record<string, number>;
  calls: Record<string, number>;
  tokens: Record<string, { input: number; output: number; thinking: number }>;
};
const EMPTY: PhaseStats = { costs: {}, times: {}, calls: {}, tokens: {} };

// THE NUMBER IS NOT WRITTEN DOWN HERE. It is the row's position in the reader's own
// pipeline, derived the way the viewer derives it (index in PIPELINE, plus two, Phase 1
// being the manual upload — which costs nothing and so has no row).
//
// It used to be a literal beside each row, and the day the repartition was moved ahead of
// the step analysis this table went on billing them 4 and 5 the old way round: the same
// call was Phase 4 in the reader and Phase 3 on this page, and nothing could tell you which
// number the costs belonged to. A phase number that follows from the pipeline cannot drift
// from it, and reordering the pipeline renumbers this table by itself.
const phaseNumber = (titleKey: string) =>
  PIPELINE.findIndex((p) => p.labelKey === titleKey) + 1;

// Each reader step maps to the backend phase(s) whose Gemini usage it covers, the
// model rows to show, and the title key(s) to join — the FIRST of which is also what
// numbers the row. Phase 1 runs the page classification + brand detection (Lancer fires
// both). Phase 2's real images have two paths: image GENERATION (the main option,
// billed per image on gemini-3.1-flash-image) and the web SEARCH backup — the search
// itself runs on Serper (credits, hence a note) and only its Flash-Lite keep/discard
// filter costs tokens.
const ETAPES: {
  titleKeys: string[];
  phases: string[];
  models?: NoticeModel[];
  serper?: boolean;
}[] = [
  { titleKeys: ['notice.step1.title'], phases: ['categorize', 'brand'] },
  // Phase 2 is the parts, in all the ways they are known: the list off the "Matériel
  // fourni" pages, and the photograph generated for each of them. They are one phase in
  // the reader now — three views of the same list — so they are one row of spend here.
  //
  // The reconstructions are NOT in it, and cannot be: they are billed by fal per run, not
  // by tokens through api_usage, and this table only knows what came through Gemini.
  {
    titleKeys: ['notice.parts.phaseTitle'],
    phases: ['parts', 'generate-image', 'filter'],
    models: [...GEN_MODELS, FILTER_MODEL],
    serper: true,
  },
  // The two questions about the steps, a row each because they are a phase each: how the
  // supplied parts are shared out among them (one call over the manual), and then what each
  // step is made of (a call per step). In that order, which is the order they run in and the
  // order the reader numbers them in. 'step-parts' belonged to no row at all before it was
  // named, which is an under-count, the worst kind of wrong number for a costs table to show.
  { titleKeys: ['notice.parts.stepsTitle'], phases: ['step-parts'] },
  { titleKeys: ['notice.merged.title'], phases: ['step-analyze'] },
  // The assembly spends now. It used to be the one phase in the reader that asked a person
  // rather than a model — no row, and nothing to put in one — and it has its own call as of
  // today: one per press, about the sub-step on screen. Small per call and unbounded in
  // count, since it is pressed rather than run, which is exactly the shape of spend a costs
  // table is for.
  { titleKeys: ['notice.assembly.title'], phases: ['sub-step-fitting'] },
];

// Sum several phase buckets into one. Cost / calls / tokens add up; the average
// time is recombined as a call-weighted mean (averaging averages would be wrong).
function mergePhases(buckets: PhaseStats[]): PhaseStats {
  const out: PhaseStats = { costs: {}, times: {}, calls: {}, tokens: {} };
  const weightedTime: Record<string, number> = {};
  for (const b of buckets) {
    for (const [m, n] of Object.entries(b.calls)) out.calls[m] = (out.calls[m] || 0) + n;
    for (const [m, c] of Object.entries(b.costs)) out.costs[m] = (out.costs[m] || 0) + c;
    for (const [m, tk] of Object.entries(b.tokens)) {
      const cur = out.tokens[m] || { input: 0, output: 0, thinking: 0 };
      out.tokens[m] = {
        input: cur.input + tk.input,
        output: cur.output + tk.output,
        thinking: cur.thinking + tk.thinking,
      };
    }
    for (const [m, avg] of Object.entries(b.times)) {
      weightedTime[m] = (weightedTime[m] || 0) + avg * (b.calls[m] || 0);
    }
  }
  for (const [m, total] of Object.entries(weightedTime)) {
    const c = out.calls[m] || 0;
    out.times[m] = c > 0 ? total / c : 0;
  }
  return out;
}

// Tarifs & Quotas: a read-only view of the per-model run economics (cost, average
// time, number of calls, token usage), one table per assembly step, plus the
// Serper credits. Gemini quota is not drawn here — it is per project and shared with
// clients we cannot see, so the Admin page links to Google's live figures instead. The
// figures below are global (across all
// documents), so they live on the Admin page rather than inside the reader. The
// model on/off control for runs stays in Phase 1.
export function PricingSection() {
  const { t } = useLanguage();
  const [phases, setPhases] = useState<Record<string, PhaseStats>>({});
  const [pricing, setPricing] = useState<Record<string, { input: number; output: number }>>({});
  const [serper, setSerper] = useState<{ used: number; total: number } | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [costs, quota] = await Promise.all([
          axios.get<{
            phases: Record<string, PhaseStats>;
            pricing: Record<string, { input: number; output: number }>;
          }>('/api/notice/costs'),
          axios.get<{ used: number; total: number }>('/api/notice/serper-quota'),
        ]);
        if (cancelled) return;
        setPhases(costs.data.phases || {});
        setPricing(costs.data.pricing || {});
        setSerper(quota.data);
      } catch {
        // non-fatal: leave the figures empty
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Grand total spent on Notice.ai: every phase's model cost, added up. It used to be a
  // sum of two things — this, and a video generator billed per second of output rather
  // than per token — and the second half went with the pipeline that spent it.
  const totalSpend = Object.values(phases).reduce(
    (sum, p) => sum + Object.values(p.costs).reduce((s, c) => s + c, 0),
    0,
  );

  return (
    <div className="mx-auto w-full max-w-3xl">
      {!loaded ? (
        <div className="flex justify-center py-8 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : (
        <div className="flex flex-col gap-14">
          {/* Grand total: sum of every phase and model. */}
          <div className="mx-auto flex w-full max-w-md flex-col items-center gap-1 rounded-2xl border border-blue-200 bg-blue-50 px-6 py-6">
            <span className="text-sm font-medium text-blue-700">
              {t('notice.pricing.totalSpent')}
            </span>
            <span className="tabular-nums text-4xl font-bold text-blue-800">
              ${totalSpend.toFixed(2)}
            </span>
          </div>

          {/* Tarifs: per-phase Gemini cost tables (Phase 2 runs on Serper). */}
          <TableCard title={t('notice.pricing.tarifs')} className="flex flex-col gap-10">
            {ETAPES.map(({ titleKeys, phases: keys, models, serper: serperPhase }) => {
              const stats = mergePhases(keys.map((k) => phases[k] || EMPTY));
              const n = phaseNumber(titleKeys[0]);
              return (
                <section key={titleKeys[0]}>
                  <h3 className="mb-3 text-center text-lg font-semibold text-slate-800">
                    {t('notice.phase')} {n}
                    {t('notice.step.sep')}
                    {titleKeys.map((k) => t(k)).join(' & ')}
                  </h3>
                  {keys.includes('generate-image') && (
                    <p className="mx-auto mb-2 max-w-md text-center text-sm text-slate-500">
                      {t('notice.pricing.genImage')}
                    </p>
                  )}
                  {serperPhase && (
                    <p className="mx-auto mb-4 max-w-md text-center text-sm text-slate-500">
                      {t('notice.pricing.serper')}
                    </p>
                  )}
                  <ModelStatsTable
                    costs={stats.costs}
                    times={stats.times}
                    calls={stats.calls}
                    tokens={stats.tokens}
                    pricing={pricing}
                    models={models}
                  />
                </section>
              );
            })}
          </TableCard>

          {/* Quotas: Serper credits + Gemini free-tier usage today. */}
          <TableCard title={t('notice.pricing.quotas')} className="flex flex-col gap-8">

            {serper && (
              <div className="mx-auto w-full max-w-md">
                <div className="mb-1.5 flex items-center justify-between text-sm">
                  <span className="font-medium text-slate-700">
                    {t('notice.pricing.serperCredits')}
                  </span>
                  <span className="tabular-nums text-slate-600">
                    {serper.used.toLocaleString()} / {serper.total.toLocaleString()}
                  </span>
                </div>
                <Bar used={serper.used} total={serper.total} />
                <p className="mt-2 text-center text-xs text-slate-400">
                  {t('notice.pricing.serperCost')}
                </p>
              </div>
            )}

            {/* Gemini quota is per PROJECT, and this app is not its only client: probes,
                scripts and AI Studio all spend the same allowance without leaving a row in our
                database. Any bar we drew from our own tables was therefore a number that could
                only be right by luck — it read 3/100 on a day the project was locked out at
                21/20. Google publishes the real figures, live, per model, so we link to them
                instead of competing with them. A missing bar asks a question; a wrong bar
                answers one. */}
            <div className="mx-auto flex w-full max-w-md flex-col gap-3">
              <a
                href="https://ai.dev/rate-limit"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-lg border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:border-blue-500 hover:text-blue-600"
              >
                {t('notice.pricing.rateLimitLink')}
                <ExternalLink className="h-4 w-4 shrink-0" />
              </a>
              <a
                href="https://console.cloud.google.com/billing/01CF4C-606092-A6C706?project=noticeia"
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between rounded-lg border border-slate-300 px-4 py-3 text-sm font-medium text-slate-700 transition-colors hover:border-blue-500 hover:text-blue-600"
              >
                {t('notice.pricing.billingLink')}
                <ExternalLink className="h-4 w-4 shrink-0" />
              </a>
              <p className="text-center text-xs text-slate-400">
                {t('notice.pricing.quotaNote')}
              </p>
            </div>
          </TableCard>
        </div>
      )}
    </div>
  );
}
